import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useChromeCompanionContext } from '../contexts/ChromeCompanionContext'
import {
  acceptChromeCompanionSetup,
  getChromeCompanionSetupKey,
  readChromeCompanionSetup,
} from '../routing/chromeCompanionSetup'
import { Button } from './ui/button'

// Shared by Tasks and Insights. The extension owns transport only.
export default function ChromeCompanionSetup({ user, children }) {
  const companion = useChromeCompanionContext()
  const key = getChromeCompanionSetupKey({
    accountId: user?.accountId || user?.account?.id,
    userId: user?.id,
  })
  const [acceptedKey, setAcceptedKey] = useState(null)
  if (!companion || (key && (acceptedKey === key || readChromeCompanionSetup(key)))) return children

  const accept = () => {
    acceptChromeCompanionSetup(key)
    setAcceptedKey(key)
  }

  return (
    <main
      className="flex h-dvh flex-col overflow-y-auto bg-background p-6 text-foreground"
      data-cy="chrome-companion-setup"
    >
      <div className="my-auto space-y-4">
        <h1 className="text-xl">ResiDesk beside your work</h1>
        <p className="text-sm">
          Open your tasks and reports without leaving your property management software.
        </p>
        <p className="text-sm">
          When you open Tasks, ResiDesk captures the page where you opened the extension to find related
          tasks. It reads page text and HTML, including content the page keeps hidden.
        </p>
        <p className="text-sm">
          ResiDesk saves that HTML on its servers to improve task matching, even when no task matches.
          It can include resident names, unit details, messages, and information stored in the page.
          Saved HTML expires after 30 days and is not sent to a third-party AI service.
        </p>
        <p className="text-sm text-muted-foreground">
          The extension does not change your property management software. Your existing ResiDesk
          permissions still apply.
        </p>
        <a
          className="block text-sm text-primary underline"
          href="https://hello.theresidesk.com/privacy-policy#google-chrome-extension"
          target="_blank"
          rel="noopener noreferrer"
        >
          Read the privacy policy
        </a>
        <Button
          className="w-full"
          disabled={!key}
          onClick={accept}
          data-cy="chrome-companion-accept"
        >
          Agree and open ResiDesk
        </Button>
        <a
          className="block text-center text-sm text-primary underline"
          href="https://www.theresidesk.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Use ResiDesk without the extension
        </a>
      </div>
    </main>
  )
}

ChromeCompanionSetup.propTypes = {
  user: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired,
}
